import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

import type { DashboardLanguage, DashboardLanguagePreference } from "../types";
import {
  DASHBOARD_LANGUAGE_CHANGE_EVENT,
  persistDashboardLanguagePreference,
  readDashboardLanguagePreference,
} from "../utils/dashboardLanguage";

interface DashboardLanguageContextValue {
  language: DashboardLanguage;
  preference: DashboardLanguagePreference;
  setPreference: (preference: DashboardLanguagePreference) => void;
}

const DashboardLanguageContext = createContext<DashboardLanguageContextValue | null>(null);

function readInitialState(): {
  preference: DashboardLanguagePreference;
  language: DashboardLanguage;
} {
  const preference = readDashboardLanguagePreference();
  persistDashboardLanguagePreference(preference, preference);
  return { preference, language: preference };
}

export function DashboardLanguageProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<DashboardLanguagePreference>(
    () => readInitialState().preference,
  );
  const [language, setLanguage] = useState<DashboardLanguage>(() => readInitialState().language);

  const applyPreference = useCallback((nextPreference: DashboardLanguagePreference) => {
    setPreferenceState(nextPreference);
    setLanguage(nextPreference);
    persistDashboardLanguagePreference(nextPreference, nextPreference);
    window.dispatchEvent(
      new CustomEvent(DASHBOARD_LANGUAGE_CHANGE_EVENT, {
        detail: { language: nextPreference, preference: nextPreference },
      }),
    );
  }, []);

  const value = useMemo(
    () => ({
      language,
      preference,
      setPreference: applyPreference,
    }),
    [applyPreference, language, preference],
  );

  return (
    <DashboardLanguageContext.Provider value={value}>{children}</DashboardLanguageContext.Provider>
  );
}

/**
 * Current language for shared leaf components that also render outside the
 * provider (isolated component tests); falls back to the stored preference.
 */
export function useDashboardLanguageOrStored(): DashboardLanguage {
  return useContext(DashboardLanguageContext)?.language ?? readDashboardLanguagePreference();
}

export function useDashboardLanguage(): DashboardLanguageContextValue {
  const context = useContext(DashboardLanguageContext);
  if (context === null) {
    throw new Error("useDashboardLanguage must be used within a DashboardLanguageProvider");
  }
  return context;
}
